import { useState } from 'react'
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query'
import { Button, Card, Fab, IconButton, Tooltip } from '@mui/material'
import { Link } from 'react-router-dom'
import PageLoading from '../../components/PageLoading/PageLoading'
import TableHeader from '../../components/TableHeader/TableHeader'
import { useNotification } from '../../context/NotificationManager/NotificationManager'
import ModalContactoLead from '../../modals/ModalContactoLead/ModalContactoLead'
import ModalContactoLeadCerrado from '../../modals/ModalContactoLeadCerrado/ModalContactoLeadCerrado'
import ModalContactoLeadCualificado from '../../modals/ModalContactoLeadCualificado/ModalContactoLeadCualificado'
import ModalContactoPrueba from '../../modals/ModalContactoPrueba/ModalContactoPrueba'
import { addContacto, getCRM, modificarPrueba } from '../../utils/api/leads'
import { getAuth } from '../../utils/auth'
import {
  compareRawDates,
  dateFromUnixTimestamp,
  dateToString,
  dateToTimeString,
  getDateRelative
} from '../../utils/date'
import { getEstadoContacto } from '../../utils/model/leads'
import { textFilter } from '../../utils/table'
import AcademiaPermitidaSelector from '../../components/form/AcademiaPermitidaSelector/AcademiaPermitidaSelector'
import { getFullHour } from '../../utils/model/horario'
import { getReservaPlaza, getValoracionPrueba } from '../../utils/documents'
import { phoneToString } from '../../utils/phone'
import css from './CRM.module.css'

const CRMColumn = ({
  title,
  isActive,
  onDragOver,
  onDragLeave,
  onDrop,
  children
}) => (
  <div
    className={css.column}
    data-status={isActive ? 'active' : 'inactive'}
    onDragOver={event => {
      event.preventDefault()
      onDragOver(title)
    }}
    onDragLeave={() => onDragLeave(title)}
    onDrop={event => {
      event.preventDefault()
      onDrop(title)
    }}
  >
    <h2 className={css.columnTitle}>{title}</h2>
    <div className={css.columnContent}>{children}</div>
  </div>
)

const CRMCard = ({
  lead,
  centro,
  isConfirmDisabled,
  onDragStart,
  onConfirmarPrueba,
  onCopyPhone
}) => {
  const {
    id,
    fecha,
    relacion,
    alumno,
    contacto,
    telefonos,
    fechaRelativa,
    estado,
    pruebaConfirmada,
    pruebaAutomatica,
    centroPrueba
  } = lead
  const primaryTelefono = telefonos?.[0]
  const isRaised = estado !== 'CLIENTE_CAPTADO'
  const status = getEstadoContacto(dateFromUnixTimestamp(fecha))
  const isPropioAlumno = relacion && relacion.toUpperCase().includes('ALUMNO')
  return (
    <Card
      draggable
      raised={isRaised}
      className={css.card}
      data-status={status}
      data-stage={estado}
      onDragStart={() => onDragStart(lead)}
    >
      <div className={css.cardTitleContainer}>
        <Link className={css.cardTitle} to={`/leads/${id}/ficha`}>
          {alumno ? (
            `${alumno}`
          ) : (
            <span className={css.placeholder}>(Lead sin nombre)</span>
          )}
        </Link>
        <div className={css.cardActions}>
          <Link
            to={`/leads/${id}/contactos`}
            className={css.actionButton}
            title='Ver contactos'
          >
            <i className='material-icons'>fact_check</i>
          </Link>
        </div>
      </div>
      {!isPropioAlumno && (
        <p className={css.cardRow}>
          <i className='material-icons'>person</i>
          <span className={css.cardInfo}>{contacto}</span>
          {relacion && <span className={css.cardInfo}> ({relacion})</span>}
        </p>
      )}
      {primaryTelefono?.telefono && (
        <p
          className={`${css.cardRow} ${css.cardRowClickable}`}
          onClick={e => {
            e.stopPropagation()
            onCopyPhone(phoneToString(primaryTelefono))
          }}
          title='Copiar teléfono'
        >
          <i className='material-icons'>phone</i>
          <span className={css.cardInfo}>{primaryTelefono.telefono}</span>
        </p>
      )}
      {fechaRelativa && (
        <p className={css.cardRow}>
          <i className='material-icons'>calendar_today</i>
          <span className={css.cardInfo}>{fechaRelativa}</span>
        </p>
      )}
      {estado === 'PRUEBA' && (
        <Button
          variant='outlined'
          size='small'
          color={pruebaConfirmada ? 'primary' : 'error'}
          className={css.confirmButton}
          onClick={() => onConfirmarPrueba(lead)}
          disabled={isConfirmDisabled}
        >
          <i className='material-icons'>
            {pruebaConfirmada ? 'check_circle' : 'psychology_alt'}
          </i>
          <span className={css.cardInfo}>
            {pruebaConfirmada ? 'Prueba confirmada' : 'Pendiente de confirmar'}
          </span>
        </Button>
      )}
      {estado === 'PRUEBA' && pruebaAutomatica && (
        <p className={css.cardRow} data-status='warning'>
          <i className='material-icons'>warning</i>
          <span className={css.cardInfo}>Prueba automática</span>
        </p>
      )}
      {estado === 'PRUEBA' && centroPrueba !== centro && (
        <p className={css.cardRow}>
          <i className='material-icons'>school</i>
          <span className={css.cardInfo}>
            Prueba en <strong>{centroPrueba}</strong>
          </span>
        </p>
      )}
    </Card>
  )
}

const CRM = () => {
  const userData = getAuth()

  const notification = useNotification()
  const queryClient = useQueryClient()

  const selectedCentro = localStorage.getItem('lastCentro')
  const [centro, setCentro] = useState(selectedCentro || userData.centro || ' ')
  const [search, setSearch] = useState('')

  const [activeLead, setActiveLead] = useState(null)
  const [activeColumn, setActiveColumn] = useState(null)
  const [dropColumn, setDropColumn] = useState(null)

  const {
    isLoading,
    data: crmColumns = {
      leads_nuevos: [],
      leads_prueba: [],
      leads_cualificados: [],
      leads_captados: [],
      leads_perdidos: [],
      leads_no_validos: [],
      leads_derivados: []
    }
  } = useQuery({
    queryKey: ['marketing', 'feedback', 'list', 'CRM', centro],
    queryFn: () =>
      getCRM(centro.trim())
        .then(leads =>
          leads.sort((l1, l2) => compareRawDates(l1.fecha, l2.fecha))
        )
        .then(leads => {
          const columns = {
            leads_nuevos: [],
            leads_prueba: [],
            leads_cualificados: [],
            leads_captados: [],
            leads_perdidos: [],
            leads_no_validos: [],
            leads_derivados: []
          }
          // Clasificamos el lead por su estado
          leads
            .map(lead => ({
              ...lead,
              fechaRelativa: getDateRelative(lead.fecha)
            }))
            .forEach(lead => {
              if (lead.estado === 'LEAD') columns.leads_nuevos.push(lead)
              else if (lead.estado === 'PRUEBA') columns.leads_prueba.push(lead)
              else if (lead.estado === 'LEAD_CUALIFICADO')
                columns.leads_cualificados.push(lead)
              else if (lead.estado === 'CLIENTE_CAPTADO')
                columns.leads_captados.push(lead)
              else if (lead.estado === 'CLIENTE_PERDIDO')
                columns.leads_perdidos.push(lead)
              else if (lead.estado === 'LEAD_NO_VALIDO')
                columns.leads_no_validos.push(lead)
              else if (lead.estado === 'LEAD_DERIVADO')
                columns.leads_derivados.push(lead)
            })
          return columns
        })
        .catch(err => {
          notification.error({
            title: 'Error al recuperar los datos del CRM',
            content: err
          })
          return {
            leads_nuevos: [],
            leads_prueba: [],
            leads_cualificados: [],
            leads_captados: [],
            leads_perdidos: [],
            leads_no_validos: [],
            leads_derivados: []
          }
        })
  })

  const { isPending: isLeadUpdating, mutate: updateLead } = useMutation({
    mutationFn: addContacto,
    onSuccess: () => {
      notification.success({
        title: 'Lead actualizado',
        content: 'Contacto añadido correctamente'
      })
      queryClient.invalidateQueries({
        queryKey: ['marketing', 'feedback', 'list', 'CRM']
      })
      setDropColumn(null)
      setActiveLead(null)
    },
    onError: err => {
      notification.error({ title: 'Error actualizando el lead', content: err })
    }
  })

  const { isPending: isConfirmingPrueba, mutateAsync: confirmarPrueba } =
    useMutation({
      mutationFn: modificarPrueba,
      onSuccess: () => {
        notification.success({
          title: 'Prueba confirmada',
          content: 'La prueba se ha modificado correctamente'
        })
        queryClient.invalidateQueries({
          queryKey: ['marketing', 'feedback', 'list', 'CRM']
        })
      },
      onError: err => {
        notification.error({
          title: 'Error modificando la prueba',
          content: err
        })
      }
    })

  const filterSearch = lead => {
    return textFilter({
      object: {
        ...lead,
        telefono: (lead.telefonos || []).map(t => t.telefono).join(' ')
      },
      fields: ['contacto', 'telefono', 'email', 'fechaRelativa'],
      search: search
    })
  }

  const handleDragOver = column => {
    if (activeColumn !== column) setActiveColumn(column)
  }

  const handleDragLeave = column => {
    if (activeColumn === column) setActiveColumn(null)
  }

  const handleDrop = column => {
    setActiveColumn(null)
    setDropColumn(column)
  }

  const handleDragStart = lead => {
    setActiveLead(lead)
  }

  const handleModalClose = () => {
    setDropColumn(null)
    setActiveLead(null)
  }

  const handleSubmitContacto = contacto => {
    if (!activeLead || isLeadUpdating) return
    updateLead({ id: activeLead.id, centroContacto: centro, ...contacto })
  }

  const handleConfirmarPrueba = lead => {
    if (!lead) return
    const { id, fecha, pruebaConfirmada } = lead
    const [dia, hora] = [dateToString(fecha), dateToTimeString(fecha)]
    const horaCompleta = getFullHour(hora)
    confirmarPrueba({
      id,
      fecha: dia,
      hora: horaCompleta,
      confirmacion: !pruebaConfirmada
    })
  }

  const handleCopyPhone = telefono => {
    navigator.clipboard
      .writeText(telefono)
      .then(() => {
        notification.success({
          title: 'Copiado',
          content: `Teléfono ${telefono} copiado al portapapeles`
        })
      })
      .catch(() => {
        notification.error({
          title: 'Error',
          content: 'No se pudo copiar el teléfono'
        })
      })
  }

  const handleDownloadReservaPlaza = () => {
    const url = getReservaPlaza({ academia: centro.trim() })
    window.open(url, '_blank')
  }

  const handleDownloadValoracionPrueba = () => {
    const url = getValoracionPrueba()
    window.open(url, '_blank')
  }

  const handleCentroChange = e => {
    const newCentro = e.target.value
    setCentro(newCentro)
    localStorage.setItem('lastCentro', newCentro)
  }

  return (
    <PageLoading isLoading={isLoading}>
      <TableHeader
        actions={
          <div className={css.actions}>
            <AcademiaPermitidaSelector
              name='academia'
              label='Academia'
              value={centro}
              onChange={handleCentroChange}
            />
            <div className={css.documentIcons}>
              <Tooltip title='Reserva de plaza'>
                <IconButton
                  color='primary'
                  onClick={handleDownloadReservaPlaza}
                  className={css.documentIcon}
                >
                  <i className='material-icons'>description</i>
                </IconButton>
              </Tooltip>

              <Tooltip title='Valoración de prueba'>
                <IconButton
                  color='primary'
                  onClick={handleDownloadValoracionPrueba}
                  className={css.documentIcon}
                >
                  <i className='material-icons'>assignment</i>
                </IconButton>
              </Tooltip>
            </div>
          </div>
        }
        title='CRM'
        search={search}
        onSearchChange={e => setSearch(e.target.value)}
      />
      <div className={css.crm}>
        <CRMColumn
          title='Nuevos Leads'
          isActive={activeColumn === 'Nuevos Leads'}
          onDragOver={handleDragOver}
          onDragLeave={handleDragLeave}
          onDrop={handleDrop}
        >
          {crmColumns.leads_nuevos.filter(filterSearch).map(lead => (
            <CRMCard
              key={lead.id}
              lead={lead}
              centro={userData.centro}
              onDragStart={handleDragStart}
              onConfirmarPrueba={handleConfirmarPrueba}
              onCopyPhone={handleCopyPhone}
            />
          ))}
        </CRMColumn>
        <CRMColumn
          title='Pruebas'
          isActive={activeColumn === 'Pruebas'}
          onDragOver={handleDragOver}
          onDragLeave={handleDragLeave}
          onDrop={handleDrop}
        >
          {crmColumns.leads_prueba.filter(filterSearch).map(lead => (
            <CRMCard
              key={lead.id}
              lead={lead}
              centro={userData.centro}
              onDragStart={handleDragStart}
              onConfirmarPrueba={handleConfirmarPrueba}
              onCopyPhone={handleCopyPhone}
              isConfirmDisabled={isConfirmingPrueba}
            />
          ))}
        </CRMColumn>
        <CRMColumn
          title='Leads Cualificados'
          isActive={activeColumn === 'Leads Cualificados'}
          onDragOver={handleDragOver}
          onDragLeave={handleDragLeave}
          onDrop={handleDrop}
        >
          {crmColumns.leads_cualificados.filter(filterSearch).map(lead => (
            <CRMCard
              key={lead.id}
              lead={lead}
              centro={userData.centro}
              onDragStart={handleDragStart}
              onCopyPhone={handleCopyPhone}
            />
          ))}
        </CRMColumn>
        <CRMColumn
          title='Leads Cerrados'
          isActive={activeColumn === 'Leads Cerrados'}
          onDragOver={handleDragOver}
          onDragLeave={handleDragLeave}
          onDrop={handleDrop}
        >
          {crmColumns.leads_captados.filter(filterSearch).map(lead => (
            <CRMCard
              key={lead.id}
              lead={lead}
              centro={userData.centro}
              onDragStart={handleDragStart}
              onCopyPhone={handleCopyPhone}
            />
          ))}
          {crmColumns.leads_perdidos.filter(filterSearch).map(lead => (
            <CRMCard
              key={lead.id}
              lead={lead}
              centro={userData.centro}
              onDragStart={handleDragStart}
              onCopyPhone={handleCopyPhone}
            />
          ))}
          {crmColumns.leads_no_validos.filter(filterSearch).map(lead => (
            <CRMCard
              key={lead.id}
              lead={lead}
              centro={userData.centro}
              onDragStart={handleDragStart}
              onCopyPhone={handleCopyPhone}
            />
          ))}
          {crmColumns.leads_derivados.filter(filterSearch).map(lead => (
            <CRMCard
              key={lead.id}
              lead={lead}
              centro={userData.centro}
              onDragStart={handleDragStart}
              onCopyPhone={handleCopyPhone}
            />
          ))}
        </CRMColumn>
      </div>
      <Fab
        className={css.fab}
        color='primary'
        aria-label='add'
        href='/nuevo-lead'
      >
        <i className='material-icons'>add</i>
      </Fab>
      <ModalContactoLead
        open={dropColumn === 'Nuevos Leads'}
        disabled={isLeadUpdating}
        onClose={handleModalClose}
        onSubmit={handleSubmitContacto}
      />
      <ModalContactoPrueba
        open={dropColumn === 'Pruebas'}
        disabled={isLeadUpdating}
        onClose={handleModalClose}
        onSubmit={handleSubmitContacto}
      />
      <ModalContactoLeadCualificado
        open={dropColumn === 'Leads Cualificados'}
        disabled={isLeadUpdating}
        onClose={handleModalClose}
        onSubmit={handleSubmitContacto}
      />
      <ModalContactoLeadCerrado
        open={dropColumn === 'Leads Cerrados'}
        disabled={isLeadUpdating}
        onClose={handleModalClose}
        onSubmit={handleSubmitContacto}
      />
    </PageLoading>
  )
}

export default CRM
